// src/components/programs/ProgramForm/Step2Sections/DayQuotationList.jsx

import React, { useMemo } from "react";
import { Box, Typography, Alert, Stack, Chip, alpha, useTheme } from "@mui/material";
import {
    WarningAmber as WarningIcon,
    // InfoOutlined as InfoIcon,
    Inventory2Outlined as ProductIcon,
} from "@mui/icons-material";
import ProductQuotationCard from "./DayQuotation/ProductQuotationCard";

const DayQuotationList = ({
    formData,
    onUpdateProductPricing,
    errors,
    isArabic,
    activeLang,
}) => {
    const theme = useTheme();

    // Get pax types array
    const paxTypesInfo = formData.pax_types || [];
    const totalPax = paxTypesInfo.reduce((sum, pt) => sum + (pt.count || 0), 0) || 0;

    /**
     * دمج المنتجات المكررة مع جمع الكميات
     */
    const mergedProducts = useMemo(() => {
        const productsMap = new Map();

        formData.itinerary?.forEach((day, dayIndex) => {
            day.products?.forEach((product, productIndex) => {
                const productId = product.product_snapshot?.product_id;
                const pricingUnit = product.product_snapshot?.pricing_unit;

                if (!productId) return;

                if (!productsMap.has(productId)) {
                    // إنشاء منتج جديد مع جمع الكميات
                    productsMap.set(productId, {
                        ...product,
                        // حفظ مواقع المنتج في الأيام المختلفة
                        locations: [{ dayIndex, productIndex }],
                        // جمع الكميات الأولية
                        merged_quantities: {
                            units: pricingUnit === 'per_unit' ? (product.dynamic_fields?.units || 0) : 0,
                            nights: pricingUnit === 'per_night' ? (product.dynamic_fields?.nights || 0) : 0,
                            persons_count: pricingUnit === 'per_person' ? 1 : 0,
                        },
                        // حفظ عدد التكرارات
                        repetition_count: 1,
                    });
                } else {
                    // المنتج موجود - نجمع الكميات
                    const existingProduct = productsMap.get(productId);

                    // إضافة الموقع الجديد
                    existingProduct.locations.push({ dayIndex, productIndex });

                    // جمع الكميات حسب نوع التسعير
                    if (pricingUnit === 'per_unit') {
                        existingProduct.merged_quantities.units += (product.dynamic_fields?.units || 0);
                    } else if (pricingUnit === 'per_night') {
                        existingProduct.merged_quantities.nights += (product.dynamic_fields?.nights || 0);
                    } else if (pricingUnit === 'per_person') {
                        existingProduct.merged_quantities.persons_count += 1;
                    }

                    // زيادة عدد التكرارات
                    existingProduct.repetition_count += 1;
                }
            });
        });

        return Array.from(productsMap.values());
    }, [formData.itinerary]);

    // Empty State
    if (mergedProducts.length === 0) {
        return (
            <Alert
                severity="warning"
                icon={<WarningIcon sx={{ fontSize: 18 }} />}
                sx={{
                    py: 1.5,
                    px: 2,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.warning.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                    '& .MuiAlert-icon': {
                        fontSize: 18,
                        mr: 1,
                    },
                    '& .MuiAlert-message': {
                        padding: 0,
                    }
                }}
            >
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                    {isArabic
                        ? "لا توجد منتجات لحساب التسعير. الرجاء إضافة منتجات في الخطوة السابقة."
                        : "No products to calculate pricing. Please add products in the previous step."}
                </Typography>
            </Alert>
        );
    }

    return (
        <Box>
            {/* Compact Header with Stats */}
            <Box
                sx={{
                    p: 1.5,
                    mb: 2,
                    borderRadius: 1.5,
                    bgcolor: alpha(theme.palette.info.main, 0.05),
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                    {/* Icon */}
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: theme.palette.info.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <ProductIcon sx={{ fontSize: 18, color: 'white' }} />
                    </Box>

                    {/* Info Text */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                            {isArabic ? "المنتجات الفريدة" : "Unique Products"}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem' }}>
                            {isArabic
                                ? `${mergedProducts.length} منتج (مع جمع الكميات)`
                                : `${mergedProducts.length} products (merged quantities)`}
                        </Typography>
                    </Box>

                    {/* Stats Chips */}
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip
                            label={`${mergedProducts.length} ${isArabic ? 'منتج' : 'Products'}`}
                            size="small"
                            color="info"
                            sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                            }}
                        />
                        <Chip
                            label={`${totalPax} ${isArabic ? 'راكب' : 'Pax'}`}
                            size="small"
                            color="primary"
                            sx={{
                                height: 24,
                                fontSize: '0.7rem',
                                fontWeight: 700,
                            }}
                        />
                    </Stack>
                </Stack>
            </Box>

            {/* Products List with Compact Spacing */}
            <Stack spacing={1.5}>
                {mergedProducts.map((product, index) => (
                    <ProductQuotationCard
                        key={product.instance_id || index}
                        product={product}
                        productIndex={index}
                        onUpdatePricing={onUpdateProductPricing}
                        errors={errors}
                        isArabic={isArabic}
                        activeLang={activeLang}
                        paxTypes={paxTypesInfo}
                    />
                ))}
            </Stack>
        </Box>
    );
};

export default DayQuotationList;
